import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, BellAlertIcon } from '@heroicons/react/24/solid';
import { COMMUNITY_LINKS } from '../../utils/constants';
import { messagingAPI } from '../../services/messagingAPI';
import { useAuth } from '../../hooks/useAuth';
import TelegramModal from './TelegramModal';

interface FloatingCommunityButtonsProps {
  showTelegramLink?: boolean;
}

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </svg>
);

const FloatingCommunityButtons: React.FC<FloatingCommunityButtonsProps> = ({ showTelegramLink = false }) => {
  const { isAuthenticated } = useAuth();
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [telegramLinked, setTelegramLinked] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !showTelegramLink) return;
    messagingAPI.getTelegramStatus().then(res => {
      if (res?.data?.isLinked) setTelegramLinked(true);
    }).catch(() => {});
  }, [isAuthenticated, showTelegramLink]);

  return (
    <>
      <div className="fixed bottom-20 right-4 sm:right-6 z-50 flex flex-col items-end gap-3">
        <AnimatePresence>
          {expanded && (
            <>
              <motion.div
                key="whatsapp-direct"
                initial={{ opacity: 0, scale: 0.7, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.7, y: 12 }}
                transition={{ duration: 0.18, delay: 0 }}
              >
                <a
                  href={COMMUNITY_LINKS.WHATSAPP_DIRECT}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2"
                  aria-label="Chat with admin on WhatsApp"
                >
                  <span className="hidden sm:block bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs font-medium px-2.5 py-1.5 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                    Chat Admin directly
                  </span>
                  <div className="relative w-12 h-12 rounded-full bg-[#128C7E] hover:bg-[#0e7366] shadow-lg flex items-center justify-center transition-transform group-hover:scale-110">
                    <WhatsAppIcon />
                    <span className="sr-only">Chat Admin on WhatsApp</span>
                    <span className="absolute top-0 right-0 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500 border-2 border-white" />
                    </span>
                  </div>
                </a>
              </motion.div>

              <motion.div
                key="telegram"
                initial={{ opacity: 0, scale: 0.7, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.7, y: 12 }}
                transition={{ duration: 0.18 }}
              >
                <button
                  onClick={() => setShowTelegramModal(true)}
                  className="group flex items-center gap-2"
                  aria-label="Telegram notifications"
                >
                  <span className="hidden sm:block bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs font-medium px-2.5 py-1.5 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                    {isAuthenticated
                      ? telegramLinked ? 'Telegram linked ✓' : 'Get order alerts on Telegram'
                      : 'Join Telegram channel'}
                  </span>
                  <div className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-transform group-hover:scale-110
                    ${telegramLinked && isAuthenticated
                      ? 'bg-green-500 hover:bg-green-600'
                      : 'bg-[#229ED9] hover:bg-[#1a8bbf]'}`}>
                    {telegramLinked && isAuthenticated
                      ? <BellAlertIcon className="w-6 h-6 text-white" />
                      : <TelegramIcon />}
                    <span className="sr-only">Telegram</span>
                  </div>
                </button>
              </motion.div>

              <motion.div
                key="whatsapp"
                initial={{ opacity: 0, scale: 0.7, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.7, y: 12 }}
                transition={{ duration: 0.18, delay: 0.05 }}
              >
                <a
                  href={COMMUNITY_LINKS.WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2"
                  aria-label="Join WhatsApp group"
                >
                  <span className="hidden sm:block bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs font-medium px-2.5 py-1.5 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                    Join WhatsApp group
                  </span>
                  <div className="w-12 h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] shadow-lg flex items-center justify-center transition-transform group-hover:scale-110">
                    <WhatsAppIcon />
                    <span className="sr-only">WhatsApp</span>
                  </div>
                </a>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={() => setExpanded(v => !v)}
          className="w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 shadow-xl flex items-center justify-center text-white transition-colors"
          aria-label="Community links"
        >
          <AnimatePresence mode="wait">
            {expanded
              ? <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <XMarkIcon className="w-6 h-6" />
                </motion.div>
              : <motion.div key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                    <path d="M20 2H4c-1.103 0-2 .897-2 2v18l4-4h14c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2z"/>
                  </svg>
                </motion.div>
            }
          </AnimatePresence>
        </motion.button>
      </div>

      <TelegramModal isOpen={showTelegramModal} onClose={() => setShowTelegramModal(false)} />
    </>
  );
};

export default FloatingCommunityButtons;
